import type { NewsFeed } from '../../../api/DigiobcanApi';
import { __, declension, formatDateTime } from '../../../utils';
import {
  feedProgress,
  feedsSummary,
  relative,
  statusMeta,
  useRunNewsFeeds,
} from './newsFeeds';
import SyncIcon from '@mui/icons-material/Sync';
import {
  Alert,
  Box,
  Button,
  Stack,
  StatusChip,
  Typography,
} from '@presov.sk/presov-ui-react';
import React from 'react';

type Props = {
  feeds: NewsFeed[];
  isLoading: boolean;
  onOpenFeed: (feed: NewsFeed) => void;
};

/**
 * Obsah pravého stĺpca kategórií noviniek: stav synchronizácie a zoznam
 * zdrojov. Adresa ani šablóny tu nie sú - všetko o zdroji je v detaile
 * (klik na zdroj), zoznam má len odpovedať na „sťahuje sa to?".
 */
const NewsFeedsPanel: React.FC<Props> = ({ feeds, isLoading, onOpenFeed }) => {
  const runFeeds = useRunNewsFeeds();
  const { lastSync, overdue, errors, busy } = feedsSummary(feeds);

  if (isLoading) {
    return (
      <Typography variant='body2' color='text.secondary'>
        {__('Načítavam zdroje…')}
      </Typography>
    );
  }

  return (
    <Stack gap={1.5}>
      {overdue ? (
        <Alert severity='error'>
          {overdue === 'never'
            ? __('Plánovač importu ešte nebežal - novinky sa nesťahujú.')
            : `${__('Plánovač importu mešká, ďalší beh mal byť')} ${relative(
                overdue,
              )}. ${__('Novinky sa nesťahujú.')}`}
        </Alert>
      ) : null}

      <Stack gap={0.25}>
        <Typography variant='body2'>
          {lastSync
            ? `${__('Naposledy synchronizované')} ${relative(lastSync)}`
            : __('Novinky sa zatiaľ nesynchronizovali.')}
        </Typography>
        {lastSync ? (
          <Typography variant='caption' color='text.secondary'>
            {formatDateTime(lastSync)}
          </Typography>
        ) : null}
        {errors ? (
          <Typography variant='caption' color='error'>
            {`${errors} ${declension(errors, [
              __('zdroj skončil chybou'),
              __('zdroje skončili chybou'),
              __('zdrojov skončilo chybou'),
            ])}`}
          </Typography>
        ) : null}
      </Stack>

      <Button
        size='small'
        variant='outline'
        startIcon={<SyncIcon />}
        disabled={busy || runFeeds.isPending}
        onClick={() => runFeeds.mutate(undefined)}
      >
        {busy ? __('Synchronizácia prebieha') : __('Synchronizovať všetko')}
      </Button>

      <Stack component='ul' sx={{ listStyle: 'none', m: 0, p: 0 }}>
        {feeds.map((feed) => {
          const { label, tone } = statusMeta(feed);
          return (
            <Box component='li' key={feed.feed_id}>
              <Box
                component='button'
                type='button'
                onClick={() => onOpenFeed(feed)}
                sx={{
                  all: 'unset',
                  boxSizing: 'border-box',
                  width: '100%',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 1,
                  py: 0.75,
                  px: 1,
                  mx: -1,
                  borderRadius: 1,
                  '&:hover, &:focus-visible': { bgcolor: 'action.hover' },
                }}
              >
                <Stack sx={{ minWidth: 0, flex: 1 }}>
                  <Typography
                    variant='body2'
                    title={feed.name}
                    sx={{
                      overflow: 'hidden',
                      textOverflow: 'ellipsis',
                      whiteSpace: 'nowrap',
                      color: feed.enabled ? undefined : 'text.secondary',
                    }}
                  >
                    {feed.name}
                  </Typography>
                  <Typography variant='caption' color='text.secondary'>
                    {feedProgress(feed)?.text ??
                      (feed.last_success_at
                        ? relative(feed.last_success_at)
                        : __('nikdy'))}
                  </Typography>
                </Stack>
                <StatusChip tone={tone}>{label}</StatusChip>
              </Box>
            </Box>
          );
        })}
      </Stack>
    </Stack>
  );
};

export default NewsFeedsPanel;
